/**
 * Aufmaß ans Projekt übertragen — pusht die verbauten Ist-Mengen je Position
 * an die Haupt-App (Softec), wo sie das Projekt-Budget (actualQuantity) speisen.
 *
 * Sammelt aus dem Store alle Positionen mit Aufmaß, baut
 * [{ positionNumber, actualQuantity: istMenge }] und POSTet an
 * POST /api/softec/aufmass-sync. Der Server ermittelt das Ziel-Projekt aus dem
 * beim Softec-Export gespeicherten softecProjectId (per slot).
 */
import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Link2, Loader2 } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import { hatAufmass, istMenge } from '@shared/aufmass';

interface SyncPayloadPosition {
  positionNumber: string;
  actualQuantity: number;
}

/** Reine Logik: Store-Positionen → Sync-Payload (nur Positionen mit Aufmaß und Positionsnummer). */
export function buildAufmassSyncPositions(
  positions: Array<{ positionNumber?: string | null; aufmass?: unknown }>,
): SyncPayloadPosition[] {
  const result: SyncPayloadPosition[] = [];
  for (const position of positions) {
    const aufmass = (position.aufmass ?? null) as Parameters<typeof hatAufmass>[0];
    if (!hatAufmass(aufmass)) continue;
    const positionNumber = (position.positionNumber ?? '').toString().trim();
    if (!positionNumber) continue;
    result.push({ positionNumber, actualQuantity: istMenge(aufmass) });
  }
  return result;
}

export function AufmassSyncButton() {
  const { toast } = useToast();
  const positions = useOfferStore((s) => s.positions);
  const currentQuoteSlot = useOfferStore((s) => s.currentQuoteSlot);
  const [isSyncing, setIsSyncing] = useState(false);

  const syncPositions = useMemo(() => buildAufmassSyncPositions(positions), [positions]);

  const hasProjektbezug = typeof currentQuoteSlot === 'number' && currentQuoteSlot > 0;
  const disabled = isSyncing || syncPositions.length === 0 || !hasProjektbezug;

  const handleSync = async () => {
    if (syncPositions.length === 0) {
      toast({
        title: 'Kein Aufmaß vorhanden',
        description: 'Es gibt keine Positionen mit erfasstem Aufmaß.',
        variant: 'destructive',
      });
      return;
    }

    if (!hasProjektbezug) {
      toast({
        title: 'Kein Projektbezug',
        description: 'Bitte das Angebot zuerst speichern und an ein Softec-Projekt exportieren.',
        variant: 'destructive',
      });
      return;
    }

    setIsSyncing(true);
    try {
      const response = await fetch('/api/softec/aufmass-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot: currentQuoteSlot, positions: syncPositions }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.success) {
        const message = payload?.error || `Server-Fehler ${response.status}`;
        throw new Error(message);
      }

      const updated = Number(payload.updated) || 0;
      const unmatched: string[] = Array.isArray(payload.unmatched) ? payload.unmatched : [];
      const beschreibung =
        unmatched.length > 0
          ? `${updated} Position(en) übertragen, ${unmatched.length} nicht zugeordnet (${unmatched.slice(0, 5).join(', ')}${unmatched.length > 5 ? '…' : ''}).`
          : `${updated} Position(en) an das Projekt übertragen.`;

      toast({
        title: 'Aufmaß übertragen',
        description: beschreibung,
        variant: unmatched.length > 0 ? 'default' : 'success',
      });
    } catch (error) {
      console.error('Aufmaß-Übertragung fehlgeschlagen:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast({
        title: 'Übertragung fehlgeschlagen',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleSync}
      disabled={disabled}
      className="w-full text-xs border-teal-200 text-teal-700 hover:bg-teal-50"
      title={
        !hasProjektbezug
          ? 'Angebot zuerst speichern und an ein Softec-Projekt exportieren'
          : syncPositions.length === 0
            ? 'Keine Position mit Aufmaß'
            : 'Verbaute Ist-Mengen an das Projekt-Budget übertragen'
      }
      data-testid="button-aufmass-sync"
    >
      {isSyncing ? (
        <>
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Übertrage Aufmaß...
        </>
      ) : (
        <>
          <Link2 className="h-3 w-3 mr-1" />
          Aufmaß ans Projekt übertragen
        </>
      )}
    </Button>
  );
}

export default AufmassSyncButton;
